import { computed, nextTick, watch } from 'vue';
import type { Ref } from 'vue';
import { useRouter } from 'vue-router';
import { JobStatus } from '@factoryfixinc/ats-interfaces';

import ConversationIndexService from '@/core/conversations/conversation-index/conversation-index.service';
import {
  isEligibleGlobalSearchQuery,
  partitionGlobalSearchResults,
} from '@/core/conversations/conversation-index/global-search.util';
import type { GlobalSearchApplicationRow } from '@/core/conversations/conversation-index/conversation-index.type';
import { getStageLabel } from '@/core/conversations/conversation-index/global-search-labels.util';
import { setPendingGlobalSearchJump } from '@/core/conversations/conversation-index/global-search-jump';
import ProjectService from '@/core/shared/project/project.service';
import { SnackbarService } from '@/core/shared/snackbar/snackbar.service';
import TrackingService from '@/core/shared/tracking/tracking.service';
import { TrackingActionName } from '@/core/shared/tracking/tracking-actions';
import { escapeHtml } from '@/utils/strings/escape-html.util';

/**
 * Wires the account-wide candidate search (ONE-6912 "Jump to any candidate")
 * into the Candidates pane: search-mode state, two-zone partitioning,
 * pre-search scroll preservation, and result selection via the existing
 * `jobApplicantId` deep-link flow.
 *
 * @param rootElementRef The pane's scroll container, or an element inside it;
 *   used to capture and restore the scroll position around search mode.
 */
// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function useGlobalCandidateSearch(rootElementRef: Ref<HTMLElement | null>) {
  const router = useRouter();
  const conversationIndexService = new ConversationIndexService();
  const projectService = new ProjectService();

  let preSearchScrollTop = 0;
  let preSearchScrollJobId: number | undefined;
  let suppressNextScrollRestore = false;

  const currentProjectJobId = computed(() => projectService.currentProject?.jobId);
  const currentJobTitle = computed(() => projectService.currentProject?.title || 'this job');

  const globalSearchQuery = computed(() => conversationIndexService.globalSearchQuery);

  /**
   * Search mode is active once the query reaches the minimum length (2 chars
   * for text, 4 digits for phone-like). Below the threshold the resting
   * pipeline content stays fully intact — no request, no empty state.
   */
  const isGlobalSearchActive = computed(() => isEligibleGlobalSearchQuery(globalSearchQuery.value));

  const globalSearchPartition = computed(() =>
    partitionGlobalSearchResults(
      conversationIndexService.globalSearchResults,
      currentProjectJobId.value,
    ),
  );

  /**
   * Loading until a response for the *current* query has settled — covers
   * both the debounce window and the request itself, so an empty state never
   * flashes before the account-wide request has actually answered. Settled
   * queries are stored trimmed, so the comparison trims too.
   */
  const isGlobalSearchLoading = computed(
    () =>
      conversationIndexService.globalSearchLoading ||
      conversationIndexService.globalSearchSettledQuery !== globalSearchQuery.value.trim(),
  );

  const isGlobalSearchError = computed(() => conversationIndexService.globalSearchError);

  /** Cross-job rows hidden by the client-side row cap (zone B only). */
  const globalSearchHasHiddenRows = computed(() => globalSearchPartition.value.hasHiddenRows);

  /**
   * True when the backend matched more candidates than the 100 it returned
   * (explicit hasMore flag, or an exact count exceeding the returned rows on
   * older backends).
   */
  const globalSearchHasMore = computed(() => conversationIndexService.globalSearchHasMore);

  /** Candidates actually returned by the endpoint. */
  const globalSearchLoadedCandidateCount = computed(
    () => conversationIndexService.globalSearchResults.length,
  );

  function findScrollParent(element: HTMLElement | null): HTMLElement | null {
    let node: HTMLElement | null = element;
    while (node) {
      const style = window.getComputedStyle(node);
      if (/(auto|scroll)/.test(style.overflowY)) return node;
      node = node.parentElement;
    }
    return null;
  }

  // Capture must be synchronous at field-open: the browser clamps the pane to
  // 0 when it focuses the autofocused input, before any watcher could observe
  // the old position (ONE-6912 iso-1 P1).
  watch(
    () => conversationIndexService.showSearchField,
    (open) => {
      if (open) {
        preSearchScrollTop = findScrollParent(rootElementRef.value)?.scrollTop ?? 0;
        preSearchScrollJobId = currentProjectJobId.value;
      }
    },
    { flush: 'sync' },
  );

  watch(isGlobalSearchActive, async (active) => {
    const scrollParent = findScrollParent(rootElementRef.value);
    if (active) {
      // A nonzero position here means the recruiter scrolled the resting list
      // after opening the field — that supersedes the open-time capture.
      if (scrollParent && scrollParent.scrollTop > 0) {
        preSearchScrollTop = scrollParent.scrollTop;
        preSearchScrollJobId = currentProjectJobId.value;
      }
      if (scrollParent) scrollParent.scrollTop = 0;
    } else {
      // The captured offset belongs to one specific list: skip the restore
      // when a jump navigated away or the project changed while search was
      // open — landing job A's offset on job B's list is worse than top.
      const skipRestore =
        suppressNextScrollRestore || currentProjectJobId.value !== preSearchScrollJobId;
      suppressNextScrollRestore = false;
      if (skipRestore) return;
      // Restore only after the DOM update that brings the resting list
      // (v-show) back into flow, or it is clamped right back to 0.
      await nextTick();
      if (scrollParent) scrollParent.scrollTop = preSearchScrollTop;
    }
  });

  /**
   * Row selection: clear the transient search state, then navigate through
   * the existing jobApplicantId deep-link flow. `useJobsRouting` switches the
   * project, aligns the status filter to the application's stage, loads the
   * list, selects the conversation, and cleans the URL. Deleted/removed jobs
   * take the existing fallback path in that flow.
   */
  async function handleGlobalSearchSelect(row: GlobalSearchApplicationRow): Promise<void> {
    const application = row.application;
    const destinationJobTitle = application.jobDisplayTitle || 'the selected job';
    const destinationStage = getStageLabel(application.applicationStatus);
    const isCurrentJob = application.jobId === currentProjectJobId.value;

    const trackingPayload = {
      scope: isCurrentJob ? 'current_job' : 'cross_job',
      job_id: application.jobId,
      application_id: application.applicationId,
      application_status: application.applicationStatus,
      job_status: application.jobStatus,
    };
    TrackingService.trackAction(
      TrackingActionName.GLOBAL_CANDIDATE_SEARCH_RESULT_SELECTED,
      trackingPayload,
    );

    // A row already labeled "Job unavailable" has no job the deep-link flow
    // could resolve — navigating would only strand the recruiter on a dead
    // `jobApplicantId` URL. Keep the search context exactly as it is and say
    // why the row cannot open, so the click never silently fails (ONE-6912).
    if (application.jobStatus === JobStatus.REMOVED) {
      TrackingService.trackAction(TrackingActionName.GLOBAL_CANDIDATE_SEARCH_JUMP_FAILED, {
        ...trackingPayload,
        error_message: 'Job no longer available',
      });
      SnackbarService.cautionAlt('This job is no longer available.');
      return;
    }

    // The jump navigates away — restoring the pre-search offset onto the
    // destination job's list would land on the wrong list.
    suppressNextScrollRestore = true;
    conversationIndexService.clearGlobalCandidateSearch();
    conversationIndexService.showSearchField = false;
    conversationIndexService.clearSelectedProAndApplication();
    // The resting list is NOT reset here: an emptied list puts the
    // infinite-scroll sentinel in view, whose fetch would race the deep link.

    // Confirmation and jump analytics fire from the deep-link flow once the
    // landing resolves; the snackbar renders HTML, so the title is escaped.
    setPendingGlobalSearchJump({
      applicationId: application.applicationId,
      message: `Jumped to ${escapeHtml(destinationJobTitle)}${
        destinationStage ? ` › ${escapeHtml(destinationStage)}` : ''
      }`,
      returnPath: router.currentRoute.value.fullPath,
      tracking: trackingPayload,
    });

    await router.push({
      path: '/jobs',
      query: {
        jobApplicantId: application.applicationId,
        jobId: application.jobId,
        // Land on the conversation tab — without an explicit tab the deep-link
        // handler falls back to the router default (resume) from `/jobs`.
        tab: 'conversation',
      },
    });
  }

  function handleGlobalSearchRetry(): void {
    conversationIndexService.searchGlobalCandidates(globalSearchQuery.value);
  }

  return {
    currentJobTitle,
    globalSearchHasHiddenRows,
    globalSearchHasMore,
    globalSearchLoadedCandidateCount,
    globalSearchPartition,
    globalSearchQuery,
    handleGlobalSearchRetry,
    handleGlobalSearchSelect,
    isGlobalSearchActive,
    isGlobalSearchError,
    isGlobalSearchLoading,
  };
}
